import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, LogIn, LogOut } from 'lucide-react';
import { toast } from 'sonner';

import { ApiError, apiErrorMessage } from '@/shared/api-client/workspace';
import { Button } from '@/shared/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/components/dialog';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/shared/ui/components/sidebar';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { useCloseSelfShift, useOpenSelfShift, useSelfShift } from './shifts';

/** Whole minutes elapsed since `openedAt`, never negative. Uncapped (ADR 0017). */
function elapsedMinutes(openedAt: string, now: number): number {
  return Math.max(0, Math.floor((now - new Date(openedAt).getTime()) / 60000));
}

/**
 * Self check-in/out on the Venue overview (ADR 0017). Gated on `workforce.shift.self` at the
 * current Venue — a user without it sees no widget at all (not a disabled control). Reads the
 * acting employee's open shift *here* from `GET /me`; shows a live `Xh Ym` duration + Check out when
 * on shift, or a single Check in otherwise. When a shift is open at another Venue, the backend
 * answers Check in with a 409 carrying that Venue; we surface a transfer dialog naming it and, on
 * confirm, re-check in with `closeExisting` to close there and open here in one atomic action (ADR 0017).
 */
export function SelfShiftWidget({ venueId }: { venueId: string }) {
  const { t } = useTranslation('common');
  const auth = useAuthorization();
  const canSelf = auth.can('workforce.shift.self', { kind: 'venue', venueId });

  const selfShift = useSelfShift(venueId, canSelf);
  const openSelf = useOpenSelfShift();
  const closeSelf = useCloseSelfShift();

  // The other Venue named by a 409 `employee_already_on_shift`; non-null opens the transfer dialog.
  const [transferTo, setTransferTo] = useState<string | null>(null);

  // Recompute the elapsed duration about once a minute so it stays current without a refresh.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  if (!canSelf) return null;

  const shift = selfShift.data ?? null;

  async function checkIn(closeExisting = false) {
    try {
      await openSelf.mutateAsync({ venueId, closeExisting });
      setTransferTo(null);
      toast.success(t('shifts.overview.checkedIn'));
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'employee_already_on_shift') {
        // A shift is open elsewhere — name it and let the user decide to transfer or cancel.
        setTransferTo(caught.details?.venueName ?? '');
        return;
      }
      toast.error(apiErrorMessage(t, 'shifts', caught));
    }
  }

  async function checkOut() {
    try {
      await closeSelf.mutateAsync({ venueId });
      toast.success(t('shifts.overview.checkedOut'));
    } catch (caught) {
      toast.error(apiErrorMessage(t, 'shifts', caught));
    }
  }

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          {shift ? (
            <>
              <SidebarMenuItem>
                <div className="flex h-8 items-center gap-2 rounded-md p-2 text-sm text-muted-foreground group-data-[collapsible=icon]:hidden">
                  <Clock className="size-4 shrink-0" aria-hidden="true" />
                  <span data-testid="self-shift-duration" className="truncate">
                    {(() => {
                      const minutes = elapsedMinutes(shift.openedAt, now);
                      return t('shifts.overview.duration', {
                        hours: Math.floor(minutes / 60),
                        minutes: minutes % 60,
                      });
                    })()}
                  </span>
                </div>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={checkOut}
                  disabled={closeSelf.isPending}
                  tooltip={t('shifts.overview.checkOut')}
                >
                  <LogOut aria-hidden="true" />
                  <span>{t('shifts.overview.checkOut')}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </>
          ) : (
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => checkIn()}
                disabled={openSelf.isPending}
                tooltip={t('shifts.overview.checkIn')}
              >
                <LogIn aria-hidden="true" />
                <span>{t('shifts.overview.checkIn')}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
        </SidebarMenu>
      </SidebarGroupContent>

      <Dialog open={transferTo !== null} onOpenChange={(open) => !open && setTransferTo(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('shifts.overview.transfer.title')}</DialogTitle>
            <DialogDescription>
              {t('shifts.overview.transfer.body', { venueName: transferTo })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setTransferTo(null)}>
              {t('shifts.overview.transfer.cancel')}
            </Button>
            <Button type="button" onClick={() => checkIn(true)} disabled={openSelf.isPending}>
              {t('shifts.overview.transfer.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarGroup>
  );
}
